/**
 * Sprint 3 — Email Automations UI
 *
 * Lists / creates / edits the scheduled automations that VoyageMarkt runs
 * against Mailchimp (Birthday, Post-trip, Anniversary, Win-back, Seasonal).
 * Each automation references an `email_drafts` row (created via the AI
 * Designer) as the template that will be sent when the trigger fires.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Calendar, Gift, RotateCw, Heart, Plane, MapPin, Sparkles, Webhook, Copy, Play, FlaskConical, Loader2, AlertCircle, CheckCircle2, Trash2, Plus, ExternalLink, Settings2 } from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  birthday:    { label: "Cumpleaños",       icon: Gift,   color: "#D95D39", desc: "Email el día del cumpleaños del subscriber" },
  post_trip:   { label: "Post-Viaje",       icon: Plane,  color: "#0A1128", desc: "X días después de la fecha de fin de viaje" },
  anniversary: { label: "Aniversario",      icon: Heart,  color: "#D95D39", desc: "Año tras año del primer viaje" },
  win_back:    { label: "Win-Back",         icon: RotateCw,color:"#8A8F98", desc: "Subscribers inactivos sin opens recientes" },
  seasonal:    { label: "Estacional",       icon: MapPin, color: "#0A1128", desc: "Calendario fijo por destino (ej. Andalucía Abril)" },
};

export default function EmailAutomations() {
  const [list, setList] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [editAuto, setEditAuto] = useState(null);
  const [runsDialog, setRunsDialog] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [a, b, c, d] = await Promise.all([
        api.get("/email-automations"),
        api.get("/email-designer/audiences").catch(() => ({ data: [] })),
        api.get("/email-designer/drafts").catch(() => ({ data: [] })),
        api.get("/email-automations/_webhook-info").catch(() => ({ data: null })),
      ]);
      setList(a.data || []);
      setAudiences(b.data || []);
      setDrafts(c.data || []);
      setWebhookInfo(d.data || null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const handleRunNow = async (a, dryRun) => {
    try {
      toast.message(dryRun ? "Probando match…" : "Lanzando automatización…");
      const r = await api.post(`/email-automations/${a.id}/run`, null, { params: { dry_run: dryRun } });
      const matches = r.data.matches ?? 0;
      if (dryRun) {
        toast.success(`Match: ${matches} suscriptor(es)${r.data.sample?.length ? ` — ej: ${r.data.sample.slice(0,3).join(", ")}` : ""}`);
      } else if (matches === 0) {
        toast.info("Sin matches hoy — no se creó campaña.");
      } else {
        toast.success(`Campaña creada en MC (${matches} destinatarios)`);
      }
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error ejecutando automatización");
    }
  };

  const handleToggleActive = async (a, active) => {
    try {
      await api.patch(`/email-automations/${a.id}`, { ...a, active });
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`¿Eliminar automatización "${a.name}"?`)) return;
    try {
      await api.delete(`/email-automations/${a.id}`);
      toast.success("Automatización eliminada");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const copyWebhookUrl = () => {
    if (webhookInfo?.url) {
      navigator.clipboard.writeText(webhookInfo.url);
      toast.success("URL copiada");
    }
  };

  return (
    <div className="space-y-6" data-testid="email-automations">
      {/* Header */}
      <Card className="edit-card p-6 rounded-sm shadow-none">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-display text-2xl text-[#0A1128] flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#D95D39]" /> Automatizaciones de email
            </h3>
            <p className="text-xs text-[#8A8F98] mt-1 max-w-2xl">
              Cada automatización se ejecuta diariamente y, cuando hay subscribers que cumplen la condición, crea automáticamente una campaña segmentada en Mailchimp (draft o send directo).
            </p>
          </div>
          <Button
            data-testid="create-auto-btn"
            className="bg-[#D95D39] hover:bg-[#c54e2e] text-white rounded-sm"
            onClick={() => setCreateOpen(true)}
          >
            <Plus className="w-4 h-4 mr-2" /> Nueva automatización
          </Button>
        </div>
      </Card>

      {/* Webhook info */}
      <Card className="edit-card p-5 rounded-sm shadow-none" data-testid="webhook-card">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-display text-lg text-[#0A1128] flex items-center gap-2">
            <Webhook className="w-4 h-4 text-[#D95D39]" /> Webhook Mailchimp → VoyageMarkt
          </h4>
          <Badge variant="outline" className={`rounded-sm ${webhookInfo?.configured ? "border-green-600 text-green-700" : "border-amber-500 text-amber-700"}`}>
            {webhookInfo?.configured ? "Configurado" : "Sin configurar"}
          </Badge>
        </div>
        <p className="text-xs text-[#8A8F98] mb-3">
          Pega la URL de abajo en Mailchimp para que cada subscribe/unsubscribe/profile actualice automáticamente tus leads en el CRM.
        </p>
        {webhookInfo?.url && (
          <div className="flex gap-2 items-center mb-2">
            <Input value={webhookInfo.url} readOnly className="font-mono text-xs rounded-sm" data-testid="webhook-url" />
            <Button size="sm" variant="outline" onClick={copyWebhookUrl} className="rounded-sm">
              <Copy className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
        <details className="text-xs text-[#8A8F98]">
          <summary className="cursor-pointer hover:text-[#0A1128]">Instrucciones</summary>
          <p className="mt-2 leading-relaxed">{webhookInfo?.instructions}</p>
        </details>
      </Card>

      {/* Automations list */}
      {loading ? (
        <Card className="edit-card p-8 rounded-sm shadow-none text-center">
          <Loader2 className="w-6 h-6 mx-auto animate-spin text-[#8A8F98]" />
        </Card>
      ) : list.length === 0 ? (
        <Card className="edit-card p-12 rounded-sm shadow-none text-center" data-testid="empty-auto">
          <Calendar className="w-12 h-12 mx-auto text-[#E2DCD0] mb-3" />
          <h4 className="font-display text-xl text-[#0A1128]">Sin automatizaciones todavía</h4>
          <p className="text-sm text-[#8A8F98] mt-1">Crea tu primera automatización (recomendado: cumpleaños).</p>
        </Card>
      ) : (
        <div className="grid gap-3">
          {list.map(a => {
            const meta = TYPE_META[a.type] || TYPE_META.seasonal;
            const Icon = meta.icon;
            return (
              <Card key={a.id} className="edit-card p-5 rounded-sm shadow-none" data-testid={`auto-${a.id}`}>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-sm flex items-center justify-center shrink-0" style={{ background: meta.color + "15" }}>
                    <Icon className="w-5 h-5" style={{ color: meta.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-medium text-[#0A1128] truncate">{a.name}</h4>
                      <Badge variant="outline" className="text-xs rounded-sm" style={{ color: meta.color, borderColor: meta.color + "60" }}>
                        {meta.label}
                      </Badge>
                      {a.auto_send && <Badge className="text-xs rounded-sm bg-red-600 hover:bg-red-700">Auto-send</Badge>}
                    </div>
                    <p className="text-xs text-[#8A8F98] mt-0.5">{meta.desc}</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-xs">
                      <Stat label="Activa" value={a.active ? "Sí" : "No"} />
                      <Stat label="Total runs" value={a.total_runs ?? 0} />
                      <Stat label="Total matches" value={a.total_matches ?? 0} />
                      <Stat label="Última" value={a.last_run_at ? new Date(a.last_run_at).toLocaleDateString("es-ES") : "—"} />
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <Switch checked={!!a.active} onCheckedChange={(v) => handleToggleActive(a, v)} data-testid={`toggle-${a.id}`} />
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="h-8 rounded-sm" onClick={() => handleRunNow(a, true)} data-testid={`dryrun-${a.id}`}>
                        <FlaskConical className="w-3.5 h-3.5 mr-1" /> Test
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 rounded-sm" onClick={() => handleRunNow(a, false)} data-testid={`run-${a.id}`}>
                        <Play className="w-3.5 h-3.5 mr-1" /> Ejecutar
                      </Button>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="h-7 rounded-sm" onClick={() => setEditAuto(a)} data-testid={`edit-${a.id}`}>
                        <Settings2 className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 rounded-sm" onClick={() => setRunsDialog(a)}>
                        Logs
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 rounded-sm text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(a)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <AutomationDialog
        open={createOpen || !!editAuto}
        onOpenChange={(o) => { if (!o) { setCreateOpen(false); setEditAuto(null); } }}
        automation={editAuto}
        audiences={audiences}
        drafts={drafts}
        onSaved={() => { setCreateOpen(false); setEditAuto(null); refresh(); }}
      />

      <RunsDialog
        automation={runsDialog}
        onOpenChange={(o) => { if (!o) setRunsDialog(null); }}
      />
    </div>
  );
}


function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-[#8A8F98] tracking-wider">{label}</div>
      <div className="text-sm text-[#0A1128] font-medium">{value}</div>
    </div>
  );
}


// ============================================================================
// Create / Edit dialog
// ============================================================================
function AutomationDialog({ open, onOpenChange, automation, audiences, drafts, onSaved }) {
  const isEdit = !!automation;
  const [form, setForm] = useState({
    name: "", type: "birthday", list_id: "", draft_id: "",
    trigger: { send_hour: 9 }, active: true, auto_send: false,
    from_name: "Espíritu Travel", reply_to: "hello@espiritutravel.com", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [mergeFields, setMergeFields] = useState([]);

  useEffect(() => {
    if (automation) setForm({ ...automation, trigger: automation.trigger || {} });
    else setForm({
      name: "", type: "birthday", list_id: "", draft_id: "",
      trigger: { send_hour: 9 }, active: true, auto_send: false,
      from_name: "Espíritu Travel", reply_to: "hello@espiritutravel.com", notes: "",
    });
  }, [automation, open]);

  // When list_id changes, fetch its merge fields so the user can pick the
  // right date field for birthday/post-trip/anniversary
  useEffect(() => {
    if (!form.list_id) { setMergeFields([]); return; }
    api.get(`/email-automations/_audience/${form.list_id}/merge-fields`)
      .then(r => setMergeFields(r.data || []))
      .catch(() => setMergeFields([]));
  }, [form.list_id]);

  const dateFields = useMemo(() => mergeFields.filter(f => f.type === "date" || f.type === "birthday"), [mergeFields]);

  const updateTrigger = (patch) => setForm(f => ({ ...f, trigger: { ...(f.trigger || {}), ...patch } }));

  const handleSave = async () => {
    if (!form.name.trim() || !form.list_id || !form.draft_id) {
      toast.error("Completa nombre, audiencia y plantilla");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/email-automations/${automation.id}`, form);
      } else {
        await api.post("/email-automations", form);
      }
      toast.success(isEdit ? "Automatización actualizada" : "Automatización creada");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  const TypeIcon = (TYPE_META[form.type] || TYPE_META.seasonal).icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <TypeIcon className="w-5 h-5 text-[#D95D39]" />
            {isEdit ? "Editar automatización" : "Nueva automatización"}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#8A8F98]">
            Configura el disparador, la audiencia y la plantilla a enviar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase text-[#8A8F98]">Nombre</Label>
            <Input data-testid="auto-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="Ej. Felicitación cumpleaños con descuento 10%" className="rounded-sm mt-1" />
          </div>

          <div>
            <Label className="text-xs uppercase text-[#8A8F98]">Tipo</Label>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mt-1">
              {Object.entries(TYPE_META).map(([k, meta]) => {
                const Icon = meta.icon;
                const sel = form.type === k;
                return (
                  <button
                    key={k}
                    type="button"
                    data-testid={`type-${k}`}
                    onClick={() => setForm({ ...form, type: k })}
                    className={`p-3 border rounded-sm text-center transition-colors ${sel ? "bg-[#D95D39]/10 border-[#D95D39]" : "border-[#E2DCD0] hover:border-[#0A1128]/40"}`}
                  >
                    <Icon className="w-5 h-5 mx-auto mb-1" style={{ color: meta.color }} />
                    <div className="text-xs font-medium text-[#0A1128]">{meta.label}</div>
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-[#8A8F98] mt-1">{TYPE_META[form.type]?.desc}</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase text-[#8A8F98]">Audiencia</Label>
              <Select value={form.list_id} onValueChange={(v) => setForm({ ...form, list_id: v })}>
                <SelectTrigger data-testid="auto-audience" className="rounded-sm mt-1"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                <SelectContent>
                  {audiences.map(a => (<SelectItem key={a.id} value={a.id}>{a.name} ({a.member_count})</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase text-[#8A8F98]">Plantilla (Draft)</Label>
              <Select value={form.draft_id} onValueChange={(v) => setForm({ ...form, draft_id: v })}>
                <SelectTrigger data-testid="auto-draft" className="rounded-sm mt-1"><SelectValue placeholder="Selecciona draft…" /></SelectTrigger>
                <SelectContent>
                  {drafts.length === 0 && <div className="text-xs p-2 text-[#8A8F98]">No hay drafts. Crea uno en &quot;AI Designer&quot;.</div>}
                  {drafts.map(d => (<SelectItem key={d.id} value={d.id}>{d.title || d.subject_line}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Type-specific trigger */}
          <div className="border-t pt-4 border-[#E2DCD0]">
            <Label className="text-xs uppercase text-[#8A8F98] tracking-wider">Configuración del disparador</Label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div>
                <Label className="text-xs">Hora de envío (UTC)</Label>
                <Input type="number" min={0} max={23} value={form.trigger?.send_hour ?? 9}
                  onChange={e => updateTrigger({ send_hour: parseInt(e.target.value, 10) || 9 })}
                  className="rounded-sm mt-1" />
              </div>

              {form.type === "birthday" && (
                <div>
                  <Label className="text-xs">Campo MC cumpleaños</Label>
                  <Select value={form.trigger?.birthday_field || ""} onValueChange={(v) => updateTrigger({ birthday_field: v })}>
                    <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="MMERGE21 (default)" /></SelectTrigger>
                    <SelectContent>
                      {dateFields.length === 0 && (
                        <div className="p-2 text-xs text-[#8A8F98]">No hay campos tipo date en esta audiencia. Añádelo en MC con tag MMERGE21.</div>
                      )}
                      {dateFields.map(f => (<SelectItem key={f.tag} value={f.tag}>{f.tag} ({f.name})</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {form.type === "post_trip" && (
                <>
                  <div>
                    <Label className="text-xs">Días después del viaje</Label>
                    <Input type="number" min={1} value={form.trigger?.days_after ?? 30}
                      onChange={e => updateTrigger({ days_after: parseInt(e.target.value, 10) || 30 })}
                      className="rounded-sm mt-1" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Campo MC fecha fin de viaje</Label>
                    <Select value={form.trigger?.trip_end_field || ""} onValueChange={(v) => updateTrigger({ trip_end_field: v })}>
                      <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                      <SelectContent>
                        {dateFields.map(f => (<SelectItem key={f.tag} value={f.tag}>{f.tag} ({f.name})</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {form.type === "anniversary" && (
                <div className="col-span-2">
                  <Label className="text-xs">Campo MC fecha primer viaje</Label>
                  <Select value={form.trigger?.first_trip_field || ""} onValueChange={(v) => updateTrigger({ first_trip_field: v })}>
                    <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                    <SelectContent>
                      {dateFields.map(f => (<SelectItem key={f.tag} value={f.tag}>{f.tag} ({f.name})</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {form.type === "win_back" && (
                <div>
                  <Label className="text-xs">Días inactivo</Label>
                  <Input type="number" min={30} value={form.trigger?.inactive_days ?? 180}
                    onChange={e => updateTrigger({ inactive_days: parseInt(e.target.value, 10) || 180 })}
                    className="rounded-sm mt-1" />
                </div>
              )}

              {form.type === "seasonal" && (
                <>
                  <div>
                    <Label className="text-xs">Fecha inicio (MM-DD)</Label>
                    <Input value={form.trigger?.start_md || ""} onChange={e => updateTrigger({ start_md: e.target.value })}
                      placeholder="04-15" className="rounded-sm mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Fecha fin (MM-DD)</Label>
                    <Input value={form.trigger?.end_md || ""} onChange={e => updateTrigger({ end_md: e.target.value })}
                      placeholder="05-31" className="rounded-sm mt-1" />
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="border-t pt-4 border-[#E2DCD0]">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">From name</Label>
                <Input value={form.from_name} onChange={e => setForm({ ...form, from_name: e.target.value })} className="rounded-sm mt-1" />
              </div>
              <div>
                <Label className="text-xs">Reply-to</Label>
                <Input value={form.reply_to} onChange={e => setForm({ ...form, reply_to: e.target.value })} className="rounded-sm mt-1" />
              </div>
              <div className="col-span-2 flex items-center justify-between border border-[#E2DCD0] rounded-sm p-3">
                <div>
                  <div className="font-medium text-sm text-[#0A1128]">Auto-send</div>
                  <p className="text-xs text-[#8A8F98]">Si está activado, la campaña se envía automáticamente. Si no, se crea como DRAFT para revisión.</p>
                </div>
                <Switch checked={!!form.auto_send} onCheckedChange={(v) => setForm({ ...form, auto_send: v })} />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Notas</Label>
                <Textarea value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="rounded-sm mt-1 text-sm" />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} className="rounded-sm bg-[#D95D39] hover:bg-[#c54e2e] text-white" data-testid="save-auto">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            {isEdit ? "Actualizar" : "Crear"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================================
// Runs (audit log) dialog
// ============================================================================
function RunsDialog({ automation, onOpenChange }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!automation) return;
    setLoading(true);
    api.get(`/email-automations/${automation.id}/runs`).then(r => setRuns(r.data || [])).finally(() => setLoading(false));
  }, [automation]);

  return (
    <Dialog open={!!automation} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto rounded-sm">
        <DialogHeader>
          <DialogTitle>Logs · {automation?.name}</DialogTitle>
          <DialogDescription className="text-xs text-[#8A8F98]">Histórico de ejecuciones, matches y campañas creadas.</DialogDescription>
        </DialogHeader>
        {loading ? <Loader2 className="w-5 h-5 mx-auto my-8 animate-spin text-[#8A8F98]" /> : runs.length === 0 ? (
          <p className="text-center text-sm text-[#8A8F98] py-8">Aún no se ha ejecutado.</p>
        ) : (
          <div className="space-y-2">
            {runs.map(r => (
              <div key={r.id} className="border border-[#E2DCD0] rounded-sm p-3 text-sm">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-[#8A8F98]">{new Date(r.ran_at).toLocaleString("es-ES")}</span>
                  <Badge variant="outline" className="text-xs rounded-sm">{r.status}</Badge>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs text-[#0A1128]">
                  <div><span className="text-[#8A8F98]">Matches:</span> <strong>{r.matches}</strong></div>
                  <div><span className="text-[#8A8F98]">Dry-run:</span> {r.dry_run ? "Sí" : "No"}</div>
                  <div>{r.campaign_id && <a href="#" className="text-[#D95D39] hover:underline">Camp {r.campaign_id.slice(0, 8)}…</a>}</div>
                </div>
                {r.match_emails_sample?.length > 0 && (
                  <div className="mt-1 text-xs text-[#8A8F98]">Sample: {r.match_emails_sample.slice(0, 5).join(", ")}</div>
                )}
                {r.error && <div className="mt-1 text-xs text-red-700">⚠ {r.error}</div>}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
